/**
 * Filters – Page orchestrator
 */

import { render, showToast } from '../../main';
import { TOAST_FILTERS_RESET, TOAST_FAILED_RESET_FILTERS } from '../../utils/app-strings';
import { preferencesApi, profileApi } from '../../api';
import { loadingSpinner, clearDebounce, flushDebounce } from '../../utils';
import type { InterestedInGender, RelationshipIntent } from '../../options';
import { FiltersState, DEFAULT_FILTERS } from './types';
import { renderFiltersForm } from './sections';
import { setupFilterHandlers } from './handlers';
import { buildDiscoveryFilters, saveFilters, updatePreferencesAndSync, DEBOUNCE_KEY } from './serialization';
import { resetBrowseSort, getBrowseMode } from '../browse';
import { navigate } from '../../router';

let currentFilters: FiltersState = { ...DEFAULT_FILTERS };

function setupFilterTabs(): void {
  document.querySelectorAll('.filters-page .seg-btn').forEach(tab => {
    tab.addEventListener('click', () => {
      const tabName = (tab as HTMLElement).dataset.tab;
      if (!tabName) return;

      // Toggle active on tabs
      document.querySelectorAll('.filters-page .seg-btn').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      // Toggle active on content panels
      document.querySelectorAll('.filters-page .seg-panel').forEach(c => c.classList.remove('active'));
      document.getElementById(`filters-tab-${tabName}`)?.classList.add('active');
    });
  });
}

function doSave(indicatorId: string): void {
  saveFilters(currentFilters, indicatorId);
}

export async function renderFilters(): Promise<void> {
  render(`
    <div class="view-frame filters-page">
      <div class="filters-header">
        <button class="btn-back" id="filters-back-btn">
          <i class="fas fa-arrow-left"></i>
        </button>
        <h1>Filters</h1>
        <button class="btn btn-text" id="filters-reset-btn">Reset</button>
      </div>
      <div class="seg-bar"><div class="seg seg-full">
        <button class="seg-btn active" data-tab="basic">Basic</button>
        <button class="seg-btn" data-tab="advanced">Advanced</button>
        <button class="seg-btn" data-tab="activity">Activity</button>
      </div></div>
      <div class="view-scroll seg-panel active" id="filters-tab-basic">
        ${loadingSpinner()}
      </div>
      <div class="view-scroll seg-panel" id="filters-tab-advanced"></div>
      <div class="view-scroll seg-panel" id="filters-tab-activity"></div>
    </div>
  `, false);

  document.getElementById('filters-back-btn')?.addEventListener('click', () => {
    navigate('/');
  });

  document.getElementById('filters-reset-btn')?.addEventListener('click', resetFilters);

  setupFilterTabs();

  // Flush any pending debounce when navigating away from filters
  const onNavigateAway = () => {
    flushDebounce(DEBOUNCE_KEY);
    window.removeEventListener('hashchange', onNavigateAway);
  };
  window.addEventListener('hashchange', onNavigateAway);

  await loadFilters();
}

/** Reset filters to defaults, applying bio preferences (gender, age, relationship goals) from profile */
async function resetFilters(): Promise<void> {
  try {
    const profileResponse = await profileApi.get();
    const bioPrefs = profileResponse.data?.profile_json?.preferences || {};

    // Start from defaults
    currentFilters = { ...DEFAULT_FILTERS };

    // Apply bio preferences - prefer intent_entries if they exist
    if (bioPrefs.intent_entries && bioPrefs.intent_entries.length > 0) {
      currentFilters.intent_entries = bioPrefs.intent_entries;
      // Also derive flat fields for backward compat
      const genders = new Set<InterestedInGender>();
      const intents = new Set<RelationshipIntent>();
      for (const entry of bioPrefs.intent_entries) {
        entry.genders?.forEach((g: InterestedInGender) => genders.add(g));
        entry.intents?.forEach((i: RelationshipIntent) => intents.add(i));
      }
      currentFilters.interested_in = Array.from(genders);
      currentFilters.relationship_intents = Array.from(intents);
    } else {
      if (bioPrefs.interested_in && bioPrefs.interested_in.length > 0) {
        currentFilters.interested_in = bioPrefs.interested_in as InterestedInGender[];
      }
      if (bioPrefs.age_min !== undefined) {
        currentFilters.age_min = bioPrefs.age_min;
      }
      if (bioPrefs.age_max !== undefined) {
        currentFilters.age_max = bioPrefs.age_max;
      }
      if (bioPrefs.relationship_intents && bioPrefs.relationship_intents.length > 0) {
        currentFilters.relationship_intents = bioPrefs.relationship_intents as RelationshipIntent[];
      }
    }

    // Re-render and save immediately (bypass debounce so navigation doesn't lose the reset)
    const isLikesMode = getBrowseMode() === 'likes';
    renderFiltersForm(currentFilters, isLikesMode);
    setupFilterHandlers(currentFilters, doSave, isLikesMode);
    clearDebounce(DEBOUNCE_KEY);
    const discoveryFilters = buildDiscoveryFilters(currentFilters, false);
    await updatePreferencesAndSync({
      age_min: currentFilters.age_min,
      age_max: currentFilters.age_max,
      distance_max_km: currentFilters.distance_max_km,
      filters_json: discoveryFilters,
    });
    resetBrowseSort();
    showToast(TOAST_FILTERS_RESET);
  } catch (err) {
    console.error('Failed to reset filters:', err);
    showToast(TOAST_FAILED_RESET_FILTERS, 'error');
  }
}

async function loadFilters(): Promise<void> {
  const content = document.getElementById('filters-tab-basic')!;

  try {
    const prefsResponse = await preferencesApi.get();
    const prefs = prefsResponse.data;
    const discovery = prefs.filters_json || {};

    // Merge discovery filters with defaults
    // Derive kids checkboxes from has_kids value
    const hasKidsValue = discovery.has_kids ?? null;
    currentFilters = {
      two_way_match: discovery.two_way_match ?? DEFAULT_FILTERS.two_way_match,
      show_human_users: discovery.show_human_users ?? DEFAULT_FILTERS.show_human_users,
      show_ai_users: discovery.show_ai_users ?? DEFAULT_FILTERS.show_ai_users,
      age_min: discovery.age_min ?? prefs.age_min ?? DEFAULT_FILTERS.age_min,
      age_max: discovery.age_max ?? prefs.age_max ?? DEFAULT_FILTERS.age_max,
      distance_max_km: discovery.distance_max_km ?? prefs.distance_max_km ?? DEFAULT_FILTERS.distance_max_km,
      interested_in: (discovery.interested_in ?? []).filter(v => v !== 'pnta'),
      relationship_intents: (discovery.relationship_intents ?? []).filter(v => v !== 'pnta'),
      recently_active_hours: discovery.recently_active_hours ?? null,
      height_min_cm: discovery.height_min_cm ?? null,
      height_max_cm: discovery.height_max_cm ?? null,
      // Kids filters
      kids_no_kids: hasKidsValue === 'must_not_have',
      kids_has_kids: hasKidsValue === 'must_have',
      kids_youngest_ages: discovery.kids_youngest_ages ?? [],
      kids_counts: discovery.kids_counts ?? [],
      kids_at_home: discovery.kids_at_home ?? null,
      has_kids: hasKidsValue,
      wants_more_kids: (discovery.wants_more_kids ?? []).filter(v => v !== 'pnta'),
      smoking: (discovery.smoking ?? []).filter(v => v !== 'pnta'),
      drinking: (discovery.drinking ?? []).filter(v => v !== 'pnta'),
      cannabis: (discovery.cannabis ?? []).filter(v => v !== 'pnta'),
      drugs: (discovery.drugs ?? []).filter(v => v !== 'pnta'),
      exercise_levels: (discovery.exercise_levels ?? []).filter(v => v !== 'pnta'),
      preferred_structure: discovery.preferred_structure ?? null,
      open_to_long_distance: (discovery.open_to_long_distance ?? []).filter(v => v !== 'pnta'),
      open_to_relocation: (discovery.open_to_relocation ?? []).filter(v => v !== 'pnta'),
      zodiac_signs: (discovery.zodiac_signs ?? []).filter(v => v !== 'pnta'),
      education_levels: (discovery.education_levels ?? []).filter(v => v !== 'pnta'),
      employment_statuses: (discovery.employment_statuses ?? []).filter(v => v !== 'pnta'),
      languages: (discovery.languages ?? []).filter(v => v !== 'pnta'),
      pets_ok_with: (discovery.pets_ok_with ?? []).filter(v => v !== 'pnta'),
      religions: (discovery.religions ?? []).filter(v => v !== 'pnta'),
      ethnicities: (discovery.ethnicities ?? []).filter(v => v !== 'pnta'),
      political_views: (discovery.political_views ?? []).filter(v => v !== 'pnta'),
      income_ranges: (discovery.income_ranges ?? []).filter(v => v !== 'pnta'),
      body_types: (discovery.body_types ?? []).filter(v => v !== 'pnta'),
      sexual_orientations: (discovery.sexual_orientations ?? []).filter(v => v !== 'pnta'),
      sexual_roles: (discovery.sexual_roles ?? []).filter(v => v !== 'pnta'),
      sexual_dynamics: (discovery.sexual_dynamics ?? []).filter(v => v !== 'pnta'),
      inclusive_of_trans_nb: discovery.inclusive_of_trans_nb ?? false,
      open_to_enm_poly: discovery.open_to_enm_poly ?? false,
      intent_entries: discovery.intent_entries ?? [],
      activity_liked_me: discovery.activity_liked_me ?? false,
      activity_i_liked: discovery.activity_i_liked ?? false,
      activity_i_disliked: discovery.activity_i_disliked ?? false,
      travel_city: discovery.travel_city ?? null,
      travel_state: discovery.travel_state ?? null,
      travel_country: discovery.travel_country ?? null,
      travel_lat: discovery.travel_lat ?? null,
      travel_lng: discovery.travel_lng ?? null,
    };

    const isLikesMode = getBrowseMode() === 'likes';
    renderFiltersForm(currentFilters, isLikesMode);
    setupFilterHandlers(currentFilters, doSave, isLikesMode);
  } catch (err: any) {
    console.error('Failed to load filters:', err);
    content.innerHTML = `
      <div class="empty-state">
        <i class="fas fa-exclamation-circle"></i>
        <h3>Failed to load filters</h3>
        <p>${err.message || 'Please try again'}</p>
        <button class="btn btn-primary" id="retry-filters-btn">Retry</button>
      </div>
    `;
    document.getElementById('retry-filters-btn')?.addEventListener('click', loadFilters);
  }
}
